import { useState } from 'react';
import type {
  RrcEp9WasteProfile,
  RrcEp9WasteProfileCreateDTO,
} from '@ots-frontend/ots-data';
import {
  OtsForm,
  OtsFormSection,
  OtsFormColumns,
  OtsFormRow,
  OtsFormActions,
  OtsFormErrorSummary,
} from '../../components/OtsForm';
import { OtsTextInput, OtsTextArea } from '../../components/OtsTextInput';
import { OtsSelect, type OtsSelectOption } from '../../components/OtsSelect';
import { OtsDateTimePicker } from '../../components/OtsDateTimePicker';
import { OtsSwitch } from '../../components/OtsSwitch';
import { OtsInlineAlert } from '../../components/OtsInlineAlert';
import { OtsEntityIdentitySelect } from '../../select/OtsEntityIdentitySelect';
import {
  ep9WasteOptions,
  toEp9WasteDraft,
  toEp9WastePayload,
  validateEp9Waste,
  type Ep9WasteDraft,
  type Ep9WasteErrors,
} from './rrc-ep9-waste-profile-ui';
export type RrcEp9WasteProfileFormProps = {
  profile?: Partial<RrcEp9WasteProfile>;
  mode?: 'create' | 'edit' | 'copy';
  objectLocked?: boolean;
  regionId?: string;
  objectOptions?: OtsSelectOption[];
  compact?: boolean;
  submitting?: boolean;
  error?: string;
  onSubmit: (payload: RrcEp9WasteProfileCreateDTO & { id?: string }) => void;
  onCancel?: () => void;
};
export const RrcEp9WasteProfileForm = ({
  profile,
  mode = 'create',
  objectLocked,
  regionId,
  objectOptions,
  compact,
  submitting,
  error,
  onSubmit,
  onCancel,
}: RrcEp9WasteProfileFormProps) => {
  const [draft, setDraft] = useState(() => toEp9WasteDraft(profile));
  const [errors, setErrors] = useState<Ep9WasteErrors>({});
  const [attempted, setAttempted] = useState(false);
  const size = compact ? 'compact' : 'default',
    options = ep9WasteOptions();
  const change = (patch: Partial<Ep9WasteDraft>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    if (attempted) setErrors(validateEp9Waste(next));
  };
  const submit = () => {
    if (submitting) return;
    const next = validateEp9Waste(draft);
    setAttempted(true);
    setErrors(next);
    if (!Object.keys(next).length)
      onSubmit(
        toEp9WastePayload(draft, mode === 'edit' ? profile?.id : undefined)
      );
  };
  const select = (
    key: keyof typeof options,
    label: string,
    disabled = false
  ) => (
    <OtsSelect
      label={label}
      required
      options={options[key]}
      value={draft[key]}
      onChange={(value) => change({ [key]: value ?? '' })}
      error={errors[key]}
      disabled={disabled}
      size={size}
      placeholder="Select an option"
    />
  );
  return (
    <OtsForm
      aria-label={
        mode === 'edit'
          ? 'Edit EP-9 waste profile'
          : mode === 'copy'
          ? 'Copy EP-9 waste profile'
          : 'New EP-9 waste profile'
      }
      noValidate
      density={size}
      disabled={submitting}
      onSubmit={submit}
      alert={
        <>
          {error && (
            <OtsInlineAlert tone="danger" title="Profile could not be saved">
              {error}
            </OtsInlineAlert>
          )}
          {attempted && <OtsFormErrorSummary errors={errors} />}
        </>
      }
      actions={
        <OtsFormActions
          onCancel={onCancel}
          submitting={submitting}
          submitLabel={
            mode === 'edit'
              ? 'Save changes'
              : mode === 'copy'
              ? 'Create version'
              : 'Create profile'
          }
          submittingLabel="Saving…"
        />
      }
    >
      <OtsFormColumns>
        <div className="space-y-4">
          <OtsFormSection
            title="Subject & waste type"
            description="The company, site, or well and waste type identify a profile series and cannot change during an edit."
          >
            <OtsEntityIdentitySelect
              entityType={draft.objectType}
              value={draft.objectId}
              entityName={profile?.objectName}
              label="Subject"
              options={objectOptions}
              regionId={regionId}
              disabled={mode === 'edit' || objectLocked}
              size={size}
              onChange={(objectId, objectType) =>
                change({ objectId: objectId ?? '', objectType })
              }
              error={errors.objectId ?? errors.objectType}
            />
            {select('wasteType', 'Waste type', mode === 'edit')}
            <OtsInlineAlert title="Generator registrations" size={size}>
              Saving resolves generator and location details from current RRC
              registrations. Company subjects need a generator registration;
              sites and wells also need a location registration.
            </OtsInlineAlert>
          </OtsFormSection>
          <OtsFormSection
            title="Effective dates"
            description="Saving an effective profile may create a replacement version. Existing manifests retain their recorded snapshot."
          >
            <OtsFormRow>
              <OtsDateTimePicker
                label="Effective start"
                required
                value={draft.start}
                onChange={(start) => change({ start })}
                error={errors.start}
                size={size}
              />
              <OtsDateTimePicker
                label="Effective end"
                value={draft.end}
                onChange={(end) => change({ end })}
                error={errors.end}
                size={size}
                description="Leave blank for no end date."
              />
            </OtsFormRow>
          </OtsFormSection>
          <OtsFormSection
            title="Contact overrides"
            description="Leave a field blank to use the registered generator contact."
          >
            {(['contactName', 'contactPhone', 'contactEmail'] as const).map(
              (key) => (
                <OtsTextInput
                  key={key}
                  label={
                    {
                      contactName: 'Contact name',
                      contactPhone: 'Contact phone',
                      contactEmail: 'Contact email',
                    }[key]
                  }
                  value={draft[key]}
                  onChange={(event) =>
                    change({ [key]: event.currentTarget.value })
                  }
                  error={errors[key]}
                  size={size}
                  type={key === 'contactEmail' ? 'email' : 'text'}
                />
              )
            )}
          </OtsFormSection>
        </div>
        <div className="space-y-4">
          <OtsFormSection title="Waste characterization">
            <OtsFormRow>
              {select(
                'wastePhysicalCharacteristics',
                'Physical characteristics'
              )}
              {select('wasteClassification', 'Classification')}
            </OtsFormRow>
            {select('wasteBasis', 'Characterization basis')}
            <OtsTextInput
              label="NORM reading (µR/hr)"
              inputMode="decimal"
              value={draft.wasteNormReading}
              onChange={(event) =>
                change({ wasteNormReading: event.currentTarget.value })
              }
              error={errors.wasteNormReading}
              size={size}
            />
            <OtsSwitch
              label="RCRA exempt"
              checked={draft.rcraExempt}
              onCheckedChange={(rcraExempt) => change({ rcraExempt })}
              size={size}
            />
            <OtsTextArea
              label="Chemical characteristics"
              required
              value={draft.wasteChemicalCharacteristics}
              onChange={(event) =>
                change({
                  wasteChemicalCharacteristics: event.currentTarget.value,
                })
              }
              error={errors.wasteChemicalCharacteristics}
              size={size}
            />
            <OtsTextArea
              label="Constituents"
              required
              value={draft.wasteConstituents}
              onChange={(event) =>
                change({ wasteConstituents: event.currentTarget.value })
              }
              error={errors.wasteConstituents}
              size={size}
            />
          </OtsFormSection>
        </div>
      </OtsFormColumns>
    </OtsForm>
  );
};
